import React from 'react'
import Grid from '@material-ui/core/Grid'
import Page from 'organization/Event/Page'
import {useOrganization} from 'organization/OrganizationProvider'
import {useRoomRoutes} from 'organization/Event/Room/RoomRoutes'
import {useRoom} from 'organization/Event/Room/RoomProvider'
import {withStyles} from '@material-ui/core/styles'
import {spacing} from 'lib/ui/theme'
import Typography from '@material-ui/core/Typography'
import {usePermissions, VIEW_RECORDINGS} from 'organization/PermissionsProvider'
import {Redirect} from 'react-router-dom'
import {api} from 'lib/url'
import MeetingCard from 'organization/Event/Room/Recordings/MeetingCard'
import {date} from 'lib/date-time'
import {RoomBreadcrumbs} from 'organization/Event/Room'
import FullPageLoader from 'lib/ui/layout/FullPageLoader'
import {useToggleArray} from 'lib/toggle'
import Button from 'lib/ui/Button'
import {MoveDown, MoveUp} from 'lib/ui/Icon'
import styled, {useTheme} from 'styled-components'
import {useInfiniteQuery} from 'react-query'
import {PaginatedCollection} from 'lib/ui/api-client'

export interface RecordingList {
  meeting_uuid: string
  recordings: Recording[]
}

export interface Recording {
  name: string
  download_url: string
  recording_start: string
  recording_end: string
  recording_type: string
  file_size: number
  file_type: string
}

export default function Recordings() {
  const roomRoutes = useRoomRoutes()
  const [showingNewestToOldest, toggleNewestToOldest] = useToggleArray()
  const {
    data,
    isPreviousData,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useRecordings(showingNewestToOldest)

  const {can} = usePermissions()

  if (!can(VIEW_RECORDINGS)) {
    return <Redirect to={roomRoutes.root} />
  }

  if (!data) {
    return (
      <Content>
        <FullPageLoader />
      </Content>
    )
  }

  const recordings = data.pages.flatMap((page) => page.data)
  const hasRecordings = recordings.length > 0

  return (
    <Content>
      <>
        <HeaderContainer>
          <Title variant="h5">Recordings</Title>
          <Sort
            hasRecordings={hasRecordings}
            showingNewestToOldest={showingNewestToOldest}
            toggleNewestToOldest={toggleNewestToOldest}
          />
        </HeaderContainer>
        <RecordingContent>
          {!hasRecordings && (
            <>
              There are no recording files available for this zoom room/meeting.{' '}
              <br />
            </>
          )}
          Please remember recordings are deleted 60 days after they are
          recorded. <br />
          We highly recommend downloading your recordings as soon as they are
          available. <br />
        </RecordingContent>
        {isPreviousData && (
          <LoaderContainer>
            <FullPageLoader />
          </LoaderContainer>
        )}
        {!isPreviousData && (
          <>
            <Grid container spacing={4}>
              <ItemsList recordings={recordings} />
            </Grid>
            {hasNextPage && (
              <LoadMoreContainer>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  aria-label="load more recordings"
                >
                  {isFetchingNextPage ? 'Loading...' : 'Load more'}
                </Button>
              </LoadMoreContainer>
            )}
          </>
        )}
      </>
    </Content>
  )
}

function Content(props: {children: JSX.Element}) {
  return (
    <RoomBreadcrumbs page="Recordings">
      <Page>{props.children}</Page>
    </RoomBreadcrumbs>
  )
}

function Sort(props: {
  hasRecordings: boolean
  showingNewestToOldest: boolean
  toggleNewestToOldest: () => void
}) {
  const {hasRecordings, showingNewestToOldest, toggleNewestToOldest} = props

  if (!hasRecordings) {
    return null
  }

  return (
    <div>
      <span>Sort:</span>
      <SortButton onClick={toggleNewestToOldest} aria-label="toggle sort view">
        <ToggleIcon isShowingNewestToOldest={showingNewestToOldest} />
      </SortButton>
    </div>
  )
}

function ItemsList(props: {recordings: RecordingList[]}) {
  const {recordings} = props

  return (
    <>
      {recordings.map((list) => (
        <Items list={list} key={list.meeting_uuid} />
      ))}
    </>
  )
}

function Items(props: {list: RecordingList}) {
  const {list} = props

  const grouped = groupByStartTime(list.recordings)

  const hasParts = Object.keys(grouped).length > 1

  const parts = Object.values(grouped)

  return (
    <>
      {parts.sort(sortByStartTimes).map((recordings, index) => (
        <Grid item xs={12} md={6} key={index}>
          <MeetingCard
            recordings={recordings}
            part={index + 1}
            hasMultipleParts={hasParts}
          />
        </Grid>
      ))}
    </>
  )
}

function sortByStartTimes(a: Recording[], b: Recording[]) {
  const aHasRecordings = a.length > 0
  const bHasRecordings = b.length > 0

  if (!aHasRecordings && bHasRecordings) {
    return -1
  }

  if (aHasRecordings && !bHasRecordings) {
    return 1
  }

  if (!aHasRecordings && !bHasRecordings) {
    return 0
  }

  const aStart = firstStart(a)
  const bStart = firstStart(b)

  if (aStart === bStart) {
    return 0
  }

  return date(aStart).isAfter(bStart) ? 1 : -1
}

function firstStart(recordings: Recording[]) {
  return recordings.reduce((acc, i) => {
    if (!acc) {
      return i.recording_start
    }

    if (acc === i.recording_start) {
      return acc
    }

    if (date(acc).isAfter(i.recording_start)) {
      return i.recording_start
    }

    return acc
  }, recordings[0].recording_start)
}

function groupByStartTime(recordings: Recording[]) {
  return recordings.reduce((acc, i) => {
    const list = acc[i.recording_start]

    const updated = list ? [...list, i] : [i]

    return {
      ...acc,
      [i.recording_start]: updated,
    }
  }, {} as Record<string, Recording[]>)
}

function useRecordings(newestFirst: boolean) {
  const {client} = useOrganization()
  const {room} = useRoom()
  const order = newestFirst ? 'desc' : 'asc'

  return useInfiniteQuery(
    ['rooms', room.id, 'recordings', order],
    ({pageParam = 1}) => {
      const params = new URLSearchParams({
        page: String(pageParam),
        per_page: '3',
        order,
      })

      return client.get<PaginatedCollection<RecordingList>>(
        api(`/rooms/${room.id}/recordings?${params}`),
      )
    },
    {
      getNextPageParam: (lastPage) =>
        lastPage.current_page < lastPage.last_page
          ? lastPage.current_page + 1
          : undefined,
      keepPreviousData: true,
    },
  )
}

function ToggleIcon(props: {isShowingNewestToOldest: boolean}) {
  const theme = useTheme()

  if (props.isShowingNewestToOldest) {
    return (
      <>
        <span>Newest to Oldest</span>
        <StyledMoveUp color={theme.colors.gray200} iconSize={20} />
      </>
    )
  }

  return (
    <>
      <span>Oldest to Newest</span>
      <StyledMoveDown color={theme.colors.gray200} iconSize={20} />
    </>
  )
}

const Title = withStyles({
  root: {
    display: 'inline-block',
  },
})(Typography)

const RecordingContent = withStyles({
  root: {
    marginBottom: spacing[8],
  },
})(Typography)

const SortButton = withStyles({
  root: {
    marginLeft: spacing[2],
  },
})(Button)

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  margin-bottom: ${(props) => props.theme.spacing[4]};
`

const LoadMoreContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${(props) => props.theme.spacing[6]};
  width: 100%;
`

const LoaderContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  min-height: 200px;
`

const StyledMoveUp = styled(MoveUp)`
  margin-left: ${(props) => props.theme.spacing[2]};
`

const StyledMoveDown = styled(MoveDown)`
  margin-left: ${(props) => props.theme.spacing[2]};
`
